import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

// ── OTA-uppdateringar av Zigbee-enheter, per gateway ─────────────────────────

export interface OtaImage {
    manufacturerCode: number | string;
    imageType: number | string;
    fileName?: string;
    fileVersion?: number | string;
    [key: string]: unknown;
}

export interface OtaDeviceStatus {
    ieeeAddr: string;
    state?: string;
    [key: string]: unknown;
}

const base = (gatewayId: string) => `${API_BASE_URL}/gateways/${gatewayId}/ota`;

export const fetchOtaImages = async (gatewayId: string): Promise<OtaImage[]> => {
    try { return (await axios.get<OtaImage[]>(`${base(gatewayId)}/images`)).data; }
    catch (e) { return handleApiError(e, 'Hämta firmwarefiler'); }
};

export const fetchOtaDevices = async (gatewayId: string): Promise<OtaDeviceStatus[]> => {
    try { return (await axios.get<OtaDeviceStatus[]>(`${base(gatewayId)}/devices`)).data; }
    catch (e) { return handleApiError(e, 'Hämta enheternas OTA-läge'); }
};

export const uploadOtaImage = async (gatewayId: string, file: File): Promise<void> => {
    try {
        const buf = await file.arrayBuffer();
        await axios.post(`${base(gatewayId)}/images`, buf, {
            headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': file.name },
        });
    } catch (e) { return handleApiError(e, 'Ladda upp firmwarefil'); }
};

export const deleteOtaImage = async (gatewayId: string, manufacturerCode: number | string, imageType: number | string): Promise<void> => {
    try { await axios.delete(`${base(gatewayId)}/images/${manufacturerCode}/${imageType}`); }
    catch (e) { return handleApiError(e, 'Ta bort firmwarefil'); }
};

/** Uppladdade filer som kan bli aktuella för enheten, och om filnamnet nämner enhetens modell. */
export interface OtaPlan {
    modelId: string | null;
    files: { fileName: string; imageType: number; fileVersion: number; modelInName: boolean }[];
}

export const fetchOtaPlan = async (gatewayId: string, ieeeAddr: string): Promise<OtaPlan> => {
    try { return (await axios.get<OtaPlan>(`${base(gatewayId)}/devices/${ieeeAddr}/plan`)).data; }
    catch (e) { return handleApiError(e, 'Hämta filer för enheten'); }
};

/**
 * Startar uppdateringen direkt; den fortsätter i bakgrunden och följs via fetchOtaDevices.
 * allowModelMismatch krävs när inget filnamn nämner enhetens modell (servern säger annars nej).
 */
export const startOtaUpdate = async (gatewayId: string, ieeeAddr: string, allowModelMismatch = false): Promise<void> => {
    try { await axios.post(`${base(gatewayId)}/devices/${ieeeAddr}/update`, { allowModelMismatch }); }
    catch (e) { return handleApiError(e, 'Starta uppdatering'); }
};

export const abortOtaUpdate = async (gatewayId: string, ieeeAddr: string): Promise<void> => {
    try { await axios.post(`${base(gatewayId)}/devices/${ieeeAddr}/abort`); }
    catch (e) { return handleApiError(e, 'Avbryt uppdatering'); }
};

